import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Field, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import wait from 'src/utils/wait';

import {
  styled,
  Grid,
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Box,
  Zoom,
  Typography,
  Divider,
  TextField,
  CircularProgress,
  Switch,
  Avatar,
  Autocomplete,
  IconButton,
  Button,
  FormControlLabel
} from '@mui/material';
import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { useSnackbar } from 'notistack';
import CloudUploadTwoToneIcon from '@mui/icons-material/CloudUploadTwoTone';
import {
  saveCustomer,
  getCustomerById,
  saveDineTable,
  parseToApiErrorMessage
} from 'src/services/apiService';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEyeTwoTone';
import RectangleTwoToneIcon from '@mui/icons-material/RectangleTwoTone';
import SquareTwoToneIcon from '@mui/icons-material/SquareTwoTone';
import {
  DineLayoutSection,
  DineTable
} from 'src/services/apiService/response-models';
import { TextField as FmTextFiled } from 'formik-mui';
import InfoPanel from 'src/components/info-panel';

const tableShapes = [
  {
    name: 'round',
    displayName: 'Round',
    icon: <PanoramaFishEyeIcon />
  },
  {
    name: 'rectangle',
    displayName: 'Rectangle',
    icon: <RectangleTwoToneIcon />
  },
  {
    name: 'square',
    displayName: 'Squre',
    icon: <SquareTwoToneIcon />
  }
];

export default function DiscountEditorDialog(props: {
  onClose: () => void;
  onSave: (e: DineLayoutSection) => void;
  sections: DineLayoutSection[];
  tableToEdit?: { sectionId: string; tableId: string };
}) {
  const { t }: { t: any } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [model, setModel] = useState<DineTable>(null);
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  const isEdit = props.tableToEdit?.tableId?.length > 0;
  const sectionNames = props.sections.map((o) => o.name);

  useEffect(() => {
    if (isEdit) {
      const sec = props.sections.find(
        (o) => o.id == props.tableToEdit.sectionId
      );
      if (!sec) {
        props.onClose();
        return;
      }

      const match = sec.tables.find((o) => o.id == props.tableToEdit.tableId);
      match.sectionName = sec.name;

      setModel({ ...match });
    }
  }, []);

  const onSave = async (updateModel: DineTable) => {
    let isSuccess = false;
    try {
      setIsSaveInProgress(true);
      updateModel.id = model?.id;
      const res = await saveDineTable(updateModel);
      enqueueSnackbar(
        t(`Table was ${!isEdit ? 'created' : 'updated'} successfully`),
        { variant: 'success' }
      );
      isSuccess = true;
      setIsSaveInProgress(false);
      props.onSave(res.data);
      return true;
    } catch (ex) {
      setIsSaveInProgress(false);
      console.log(ex);
      const msg = parseToApiErrorMessage(
        ex,
        `Failed to ${!isEdit ? 'created' : 'updated'} table`
      );
      enqueueSnackbar(t(msg), { variant: 'error' });
      return false;
    }
  };

  const handleClose = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    props.onClose();
  };

  return (
    <Dialog open={true} onClose={handleClose} disableEscapeKeyDown>
      <DialogTitle
        sx={{
          p: 3
        }}
      >
        <Typography variant="h4" gutterBottom>
          {isEdit ? t('Edit discount') : t('Add discount')}
        </Typography>
        <Typography variant="subtitle2">
          {t('Fill in the fields below to create/edit discount code')}
        </Typography>
      </DialogTitle>
      <Formik
        enableReinitialize={true}
        initialValues={{
          code: model?.sectionName || '',
          description: '',
          isPercentage: true,
          value: 0,
          isActive: model?.isActive === false ? false : true
        }}
        validationSchema={Yup.object().shape({
          // username: Yup.string()
          //   .max(255)
          //   .required(t('The username field is required')),
          sectionName: Yup.string()
            .max(255)
            .required(t('The section name field is required')),
          tableName: Yup.string()
            .max(255)
            .required(t('The table name field is required')),
          tableShape: Yup.string()
            .max(255)
            .required(t('The table shape field is required')),
          capacity: Yup.number()
            .max(999)
            .min(1)
            .required(t('The table shape field is required'))
        })}
        onSubmit={async (
          _values,
          { resetForm, setErrors, setStatus, setSubmitting }
        ) => {
          try {
            //  var isSuccess = await onSave({ ..._values });
            //  if (isSuccess) {
            resetForm();
            setStatus({ success: true });
            // }
          } catch (err) {
            console.error(err);
            setStatus({ success: false });
          }
          setSubmitting(false);
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent
              dividers
              sx={{
                p: 3
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Field
                    fullWidth
                    name="code"
                    component={FmTextFiled}
                    label="Discount code *"
                    placeholder={t('enter discount code here...')}
                    helperText={errors.code}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    fullWidth
                    name="code"
                    component={FmTextFiled}
                    label="Description"
                    placeholder={t('enter discount description here...')}
                    helperText={errors.description}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControlLabel
                    labelPlacement="start"
                    control={
                      <Switch
                        name="isPercentage"
                        disabled={!isEdit}
                        color={'primary'}
                        defaultChecked
                        onBlur={handleBlur}
                        onChange={handleChange}
                        checked={values.isActive}
                      />
                    }
                    label={t('Is Percentage')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    fullWidth
                    name="value"
                    type="number"
                    component={FmTextFiled}
                    label={
                      !values.isPercentage
                        ? 'Discount in percent *'
                        : 'Discount fixed amount *'
                    }
                    placeholder={t(
                      `enter discount ${
                        values.isPercentage ? 'percent' : 'amount'
                      } here...`
                    )}
                    helperText={errors.value}
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoPanel title="2% discount will be applied" />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions
              sx={{
                p: 3
              }}
            >
              <Button
                color="secondary"
                onClick={props.onClose}
                disabled={Boolean(isSaveInProgress) || isSubmitting}
              >
                {t('Cancel')}
              </Button>
              <Button
                type="submit"
                startIcon={
                  isSubmitting || isSaveInProgress ? (
                    <CircularProgress size="1rem" />
                  ) : null
                }
                disabled={Boolean(isSaveInProgress) || isSubmitting}
                variant="contained"
              >
                {t('Save')}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
}
